import {
  DayFlag,
  DayPicker,
  type DayPickerProps,
  type DayProps,
  SelectionState,
  UI,
} from 'react-day-picker'

import { ChevronDownIcon } from '@gto/icons-react/solid/chevron-down'
import { ChevronLeftIcon } from '@gto/icons-react/solid/chevron-left'
import { ChevronRightIcon } from '@gto/icons-react/solid/chevron-right'
import { ChevronUpIcon } from '@gto/icons-react/solid/chevron-up'
import { cn } from '@gto/ui/cn'

import { calendarVariants } from './calendar.styles'

/**
 * Props for a custom day cell, re-exported so a consumer can swap `components.Day`
 * without taking a direct dependency on react-day-picker.
 */
export type CalendarDayProps = DayProps

export type CalendarProps = DayPickerProps & {
  /**
   * Earliest selectable day — bounds navigation and disables everything before it.
   *
   * Named `minDate`, not `min`: react-day-picker already uses `min`/`max` for the
   * count of selectable days in `multiple` mode.
   */
  minDate?: Date
  /** Latest selectable day — bounds navigation and disables everything after it. */
  maxDate?: Date
  /** `small` the desktop grid (default); `medium` the touch sheet — 32px month buttons, `body2` weekdays, days 2px apart. */
  size?: 'small' | 'medium'
}

const CHEVRON_BY_ORIENTATION = {
  up: ChevronUpIcon,
  down: ChevronDownIcon,
  left: ChevronLeftIcon,
  right: ChevronRightIcon,
}

const Chevron = ({ orientation }: { orientation?: 'up' | 'down' | 'left' | 'right' }) => {
  const Icon = CHEVRON_BY_ORIENTATION[orientation ?? 'left']

  return <Icon className="size-4" />
}

/**
 * Tailwind-token calendar over `react-day-picker`.
 *
 * Base UI has no calendar primitive (`mui/base-ui#1709` is still open), so the
 * grid, range logic, and roving-focus keyboard model come from react-day-picker
 * while the surrounding popover stays Base UI.
 *
 * `role="gridcell"` / `aria-selected` sit on the `<td>` and `disabled` on the
 * inner `<button>` — react-day-picker's standard grid DOM, which differs from
 * MUI X's (role on the button). Consumers asserting the old shape need
 * `gridcell.querySelector('button')`.
 */
export function Calendar({
  minDate,
  maxDate,
  size = 'small',
  disabled,
  className,
  classNames,
  components,
  formatters,
  labels,
  ...props
}: CalendarProps) {
  const styles = calendarVariants({ size })

  const bounds = [
    ...(Array.isArray(disabled) ? disabled : disabled == null ? [] : [disabled]),
    ...(minDate ? [{ before: minDate }] : []),
    ...(maxDate ? [{ after: maxDate }] : []),
  ]

  return (
    <DayPicker
      {...props}
      className={cn(styles.root(), className)}
      components={{ Chevron, ...components }}
      disabled={bounds.length > 0 ? bounds : undefined}
      endMonth={props.endMonth ?? maxDate}
      startMonth={props.startMonth ?? minDate}
      classNames={{
        [UI.Months]: styles.months(),
        [UI.Month]: styles.month(),
        [UI.Nav]: styles.nav(),
        [UI.PreviousMonthButton]: styles.navButton(),
        [UI.NextMonthButton]: styles.navButton(),
        [UI.MonthCaption]: styles.monthCaption(),
        [UI.CaptionLabel]: styles.captionLabel(),
        [UI.MonthGrid]: styles.monthGrid(),
        [UI.Weekdays]: styles.weekdays(),
        [UI.Weekday]: styles.weekday(),
        [UI.Weeks]: styles.weeks(),
        [UI.Week]: styles.week(),
        [UI.Day]: styles.day(),
        [UI.DayButton]: styles.dayButton(),
        [DayFlag.today]: styles.today(),
        [DayFlag.outside]: styles.outside(),
        [DayFlag.disabled]: styles.disabled(),
        [DayFlag.hidden]: styles.hidden(),
        [SelectionState.selected]: styles.selected(),
        [SelectionState.range_start]: styles.rangeStart(),
        [SelectionState.range_middle]: styles.rangeMiddle(),
        [SelectionState.range_end]: styles.rangeEnd(),
        ...classNames,
      }}
      // Single-letter column headers, per the design; react-day-picker's default
      // is `cccccc` (two letters). Formatted through its own `dateLib` so the
      // initials follow the active locale rather than pinning en-US.
      formatters={{
        formatWeekdayName: (date, _options, dateLib) =>
          dateLib?.format(date, 'ccccc') ?? date.toLocaleDateString('en-US', { weekday: 'narrow' }),
        ...formatters,
      }}
      // Terser than react-day-picker's "Go to the Previous Month" default, and the
      // wording existing e2e specs already look for.
      labels={{
        labelPrevious: () => 'Previous month',
        labelNext: () => 'Next month',
        ...labels,
      }}
    />
  )
}
